import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ChevronRight,
  Crown,
  FileText,
  Mail,
  Shield,
  ShoppingBag,
  Smartphone,
  Sparkles,
  Swords,
  Trophy,
  Users,
} from "lucide-react";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/components/ui/sonner";
import { Textarea } from "@/components/ui/textarea";
import {
  CONTACT_EMAIL,
  HOME_META,
  PRIVACY_PATH,
  REGULATION_PATH,
  REGISTRATIONS_OPEN,
  REGISTRATIONS_CLOSED_MESSAGE,
  SITE_DOMAIN,
} from "@/content/siteContent";
import { useActiveSection } from "@/hooks/useActiveSection";
import { usePageSEO } from "@/hooks/usePageSEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NAV_ITEMS = [
  { id: "home", label: "Home" },
  { id: "perche", label: "Perché" },
  { id: "premi", label: "Premi" },
  { id: "ticket", label: "Ticket" },
  { id: "come-funziona", label: "Come funziona" },
  { id: "per-chi", label: "Per chi" },
  { id: "lancio", label: "Lancio" },
  { id: "faq", label: "FAQ" },
  { id: "contatti", label: "Contatti" },
];

const VALUE_CARDS = [
  {
    icon: Crown,
    title: "Competizione premium",
    description: "Un torneo fantacalcio online costruito per chi vuole misurarsi davvero, con regole chiare e posizionamento esclusivo.",
  },
  {
    icon: Trophy,
    title: "Premi esclusivi",
    description: "Premi reali distribuiti lungo il Trono Reale e nelle competizioni collaterali per dare valore a ogni fase della stagione.",
  },
  {
    icon: Sparkles,
    title: "Esperienza coinvolgente",
    description: "Ogni giornata conta: ritmo, strategia, tensione e desiderio di avanzare verso il podio.",
  },
  {
    icon: Users,
    title: "Community di appassionati",
    description: "Un ambiente selettivo per chi vive calcio, analisi e voglia di primeggiare in una sfida più ambiziosa del solito.",
  },
];

const PRIZES = [
  {
    key: "first",
    rank: "1° Premio",
    title: "Honda SH 300",
    label: "Trono Reale",
    description: "Il premio simbolo di Fanta Royal: una ricompensa di forte presenza che consacra il vertice della stagione.",
    className: "podium-card podium-card--first",
    orderClass: "lg:order-2",
    testId: "prize-first-card",
  },
  {
    key: "second",
    rank: "2° Premio",
    title: "Piaggio Liberty 50",
    label: "Prestigio da podio",
    description: "Una ricompensa di forte valore simbolico per chi chiude la stagione immediatamente alle spalle del trono.",
    className: "podium-card podium-card--secondary",
    orderClass: "lg:order-1",
    testId: "prize-second-card",
  },
  {
    key: "third",
    rank: "3° Premio",
    title: "Crociera MSC",
    label: "Esperienza esclusiva",
    description: "Un premio memorabile per chi conquista il podio con continuità, lucidità e gestione di alto livello.",
    className: "podium-card podium-card--secondary",
    orderClass: "lg:order-3",
    testId: "prize-third-card",
  },
];

const HOW_IT_WORKS = [
  {
    icon: Shield,
    step: "01",
    title: "Acquista il ticket",
    description: "Completa l’ingresso con un pagamento una tantum, protetto da Stripe Checkout e supportato da trust message chiari.",
  },
  {
    icon: Crown,
    step: "02",
    title: "Leggi il regolamento e prepara la rosa",
    description: "Hai regole, struttura e premi sempre accessibili prima di entrare davvero nella competizione.",
  },
  {
    icon: Smartphone,
    step: "03",
    title: "Segui ogni giornata",
    description: "Vivi il campionato con ritmo, pressione e attenzione ai dettagli da ogni dispositivo.",
  },
  {
    icon: Swords,
    step: "04",
    title: "Scala la classifica e punta al trono",
    description: "Costanza, coraggio e sangue freddo: è così che si arriva davvero al vertice.",
  },
];

const AUDIENCE_CARDS = [
  {
    title: "Per chi vuole competere davvero",
    description: "Qui ogni scelta viene misurata. Il margine tra podio e anonimato è sottile e proprio per questo conta di più.",
  },
  {
    title: "Per chi ama strategia e calcio",
    description: "Non basta conoscere i nomi: serve leggere il campionato, prevedere gli equilibri e gestire il rischio con lucidità.",
  },
  {
    title: "Per chi vuole puntare ai grandi premi",
    description: "Fanta Royal premia l’ambizione con una gerarchia chiara, una struttura ordinata e riconoscimenti di forte impatto.",
  },
];

const FAQ_ITEMS = [
  {
    question: "Come posso acquistare il ticket?",
    answer:
      "Compila il modulo ticket, consulta Regolamento e Privacy, accetta le condizioni richieste e verrai reindirizzato al checkout Stripe.",
  },
  {
    question: "Qual è la quota di ingresso?",
    answer:
      "Il Ticket Fanta Royal è una quota una tantum pari a 70 EUR, definita lato backend per garantire trasparenza e coerenza del flusso di pagamento.",
  },
  {
    question: "Posso pagare con Apple Pay o Google Pay?",
    answer:
      "Sì, se il tuo dispositivo e il tuo metodo di pagamento sono supportati da Stripe Checkout, vedrai automaticamente le opzioni disponibili.",
  },
  {
    question: "Sono previsti rimborsi dopo la conferma dell’iscrizione?",
    answer:
      "Salvo diversa decisione espressa dell’organizzazione, il regolamento prevede che non siano normalmente previsti rimborsi dopo la conferma dell’iscrizione.",
  },
];

const HERO_BACKGROUND =
  "https://images.unsplash.com/photo-1700831212888-c1e23eeaf129?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxmb290YmFsbCUyMHN0YWRpdW0lMjBuaWdodCUyMGxpZ2h0cyUyMGNyb3dkJTIwZGFya3xlbnwwfHx8YmxhY2t8MTc3NjI2NDMyOXww&ixlib=rb-4.1.0&q=85";

const HERO_TRUST_CHIPS = [
  "Pagamento sicuro Stripe",
  "Quota 70 €",
  "Carta • Apple Pay • Google Pay",
];

const REGULATION_POINTS = [
  "Dominio ufficiale: ilfantaroyal.it",
  "Regolamento e Privacy sempre accessibili",
  "Conferma ordine solo dopo webhook Stripe",
];

const initialLeadFormState = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  message: "",
  privacy_accepted: false,
};

const initialCheckoutFormState = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  accepted_rules: false,
  accepted_privacy: false,
};

const defaultTicketConfig = {
  product_name: "Ticket Fanta Royal",
  amount: 70,
  currency: "EUR",
  display_price: "70 €",
  payment_methods_hint: "Carta, Apple Pay e Google Pay se disponibili sul dispositivo",
  trust_points: [
    "Pagamento una tantum",
    "Valuta EUR",
    "Conferma ordine dopo webhook Stripe",
    "Regolamento disponibile prima dell’acquisto",
  ],
  stripe_ready: false,
};

const phonePattern = /^[+0-9()\-\s]{7,20}$/;

const createLaunchTarget = () => new Date("2026-09-04T00:00:00+02:00");

const formatCountdown = (targetDate) => {
  const distance = targetDate.getTime() - Date.now();

  if (distance <= 0) {
    return { days: "00", hours: "00", minutes: "00", seconds: "00" };
  }

  const days = Math.floor(distance / (1000 * 60 * 60 * 24));
  const hours = Math.floor((distance / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((distance / (1000 * 60)) % 60);
  const seconds = Math.floor((distance / 1000) % 60);

  return {
    days: String(days).padStart(2, "0"),
    hours: String(hours).padStart(2, "0"),
    minutes: String(minutes).padStart(2, "0"),
    seconds: String(seconds).padStart(2, "0"),
  };
};

const Reveal = ({ children, delay = 0, className = "" }) => {
  const prefersReducedMotion = useReducedMotion();

  if (prefersReducedMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
};

const CrownMotif = ({ className = "" }) => (
  <svg
    viewBox="0 0 224 84"
    fill="none"
    aria-hidden="true"
    data-testid="decorative-crown-motif"
    className={className}
  >
    <path
      d="M10 67.5 38 26l34 29 40-44 40 44 34-29 28 41.5"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M24 67.5h176" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    <circle cx="38" cy="26" r="6" stroke="currentColor" strokeWidth="2.5" />
    <circle cx="112" cy="11" r="8" stroke="currentColor" strokeWidth="2.5" />
    <circle cx="186" cy="26" r="6" stroke="currentColor" strokeWidth="2.5" />
  </svg>
);

const SectionHeading = ({ eyebrow, title, description, align = "left" }) => (
  <div className={`section-heading ${align === "center" ? "section-heading--center" : ""}`}>
    {eyebrow ? <span className="section-kicker">{eyebrow}</span> : null}
    <h2 className="section-title">{title}</h2>
    {description ? <p className="section-description">{description}</p> : null}
  </div>
);

const CountdownBlock = () => {
  const launchTarget = useMemo(() => createLaunchTarget(), []);
  const [countdown, setCountdown] = useState(() => formatCountdown(launchTarget));

  useEffect(() => {
    const timer = window.setInterval(() => {
      setCountdown(formatCountdown(launchTarget));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [launchTarget]);

  const entries = [
    { label: "Giorni", value: countdown.days },
    { label: "Ore", value: countdown.hours },
    { label: "Minuti", value: countdown.minutes },
    { label: "Secondi", value: countdown.seconds },
  ];

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
      {entries.map((entry) => (
        <div key={entry.label} className="countdown-tile" data-testid="countdown-tile">
          <span className="countdown-value">{entry.value}</span>
          <span className="countdown-label">{entry.label}</span>
        </div>
      ))}
    </div>
  );
};

const scrollToSection = (id) => {
  const section = document.getElementById(id);
  if (section) {
    section.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

export default function LandingPage() {
  usePageSEO({
    title: HOME_META.title,
    description: HOME_META.description,
    canonicalPath: "/",
    keywords: HOME_META.keywords,
  });

  const activeSection = useActiveSection(NAV_ITEMS.map((item) => item.id));
  const prefersReducedMotion = useReducedMotion();

  const [ticketConfig, setTicketConfig] = useState(defaultTicketConfig);
  const [ticketConfigLoaded, setTicketConfigLoaded] = useState(false);
  const [leadFormState, setLeadFormState] = useState(initialLeadFormState);
  const [leadErrors, setLeadErrors] = useState({});
  const [leadSubmitState, setLeadSubmitState] = useState({ status: "idle", message: "" });
  const [checkoutForm, setCheckoutForm] = useState(initialCheckoutFormState);
  const [checkoutErrors, setCheckoutErrors] = useState({});
  const [checkoutState, setCheckoutState] = useState({ status: "idle", message: "" });

  useEffect(() => {
    const loadTicketConfig = async () => {
      try {
        const response = await axios.get(`${API}/payments/ticket-config`);
        setTicketConfig(response.data);
      } catch (error) {
        // Silently fail - ticket config will use defaults
      } finally {
        setTicketConfigLoaded(true);
      }
    };

    loadTicketConfig();
  }, []);

  const heroAnimation = prefersReducedMotion
    ? {}
    : {
        initial: { opacity: 0, y: 28 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] },
      };

  const isCheckoutLegallyReady = checkoutForm.accepted_rules && checkoutForm.accepted_privacy;

  const handleLeadFieldChange = (event) => {
    const { name, value } = event.target;
    setLeadFormState((current) => ({ ...current, [name]: value }));
    setLeadErrors((current) => ({ ...current, [name]: "" }));
  };

  const handleLeadPrivacyChange = (checked) => {
    setLeadFormState((current) => ({ ...current, privacy_accepted: Boolean(checked) }));
    setLeadErrors((current) => ({ ...current, privacy_accepted: "" }));
  };

  const handleCheckoutFieldChange = (event) => {
    const { name, value } = event.target;
    setCheckoutForm((current) => ({ ...current, [name]: value }));
    setCheckoutErrors((current) => ({ ...current, [name]: "" }));
  };

  const handleCheckoutCheckboxChange = (field, checked) => {
    setCheckoutForm((current) => ({ ...current, [field]: Boolean(checked) }));
    setCheckoutErrors((current) => ({ ...current, [field]: "" }));
  };

  const validateLeadForm = () => {
    const nextErrors = {};

    if (!leadFormState.first_name.trim()) nextErrors.first_name = "Inserisci il nome.";
    if (!leadFormState.last_name.trim()) nextErrors.last_name = "Inserisci il cognome.";
    if (!leadFormState.email.trim()) {
      nextErrors.email = "Inserisci l’email.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(leadFormState.email)) {
      nextErrors.email = "Inserisci un’email valida.";
    }
    if (leadFormState.phone.trim() && !phonePattern.test(leadFormState.phone.trim())) {
      nextErrors.phone = "Inserisci un numero di telefono valido.";
    }
    if (!leadFormState.privacy_accepted) nextErrors.privacy_accepted = "Devi accettare l’informativa privacy.";
    return nextErrors;
  };

  const validateCheckoutForm = () => {
    const nextErrors = {};

    if (!checkoutForm.first_name.trim()) nextErrors.first_name = "Inserisci il nome.";
    if (!checkoutForm.last_name.trim()) nextErrors.last_name = "Inserisci il cognome.";
    if (!checkoutForm.email.trim()) {
      nextErrors.email = "Inserisci l’email.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(checkoutForm.email)) {
      nextErrors.email = "Inserisci un’email valida.";
    }
    if (checkoutForm.phone.trim() && !phonePattern.test(checkoutForm.phone.trim())) {
      nextErrors.phone = "Inserisci un numero di telefono valido.";
    }
    if (!checkoutForm.accepted_rules) {
      nextErrors.accepted_rules = "Per procedere devi accettare il Regolamento di Fanta Royal.";
    }
    if (!checkoutForm.accepted_privacy) {
      nextErrors.accepted_privacy = "Per procedere devi dichiarare di aver letto l’Informativa Privacy.";
    }

    return nextErrors;
  };

  const handleLeadSubmit = async (event) => {
    event.preventDefault();
    const validationErrors = validateLeadForm();

    if (Object.keys(validationErrors).length > 0) {
      setLeadErrors(validationErrors);
      setLeadSubmitState({ status: "error", message: "Controlla i campi evidenziati e riprova." });
      toast.error("Controlla i dati inseriti.");
      return;
    }

    setLeadErrors({});
    setLeadSubmitState({ status: "loading", message: "Invio in corso…" });

    try {
      const payload = {
        ...leadFormState,
        message: leadFormState.message.trim() || null,
        phone: leadFormState.phone.trim() || null,
        source: "landing-page",
      };
      const response = await axios.post(`${API}/leads`, payload);
      setLeadSubmitState({ status: "success", message: response.data.message });
      setLeadFormState(initialLeadFormState);
      toast.success("Richiesta inviata con successo.");
    } catch (error) {
      const apiMessage = error.response?.data?.detail || "Non siamo riusciti a registrare la tua richiesta. Riprova tra poco.";
      setLeadSubmitState({ status: "error", message: apiMessage });
      toast.error(apiMessage);
    }
  };

  const handleCheckoutSubmit = async (event) => {
    event.preventDefault();
    const validationErrors = validateCheckoutForm();

    if (Object.keys(validationErrors).length > 0) {
      setCheckoutErrors(validationErrors);
      setCheckoutState({ status: "error", message: "Completa correttamente i dati e le accettazioni richieste per procedere al checkout." });
      toast.error("Controlla i dati del ticket.");
      return;
    }

    setCheckoutErrors({});
    setCheckoutState({ status: "loading", message: "Stiamo preparando il checkout sicuro…" });

    try {
      const payload = {
        origin_url: window.location.origin,
        first_name: checkoutForm.first_name.trim(),
        last_name: checkoutForm.last_name.trim(),
        email: checkoutForm.email.trim(),
        phone: checkoutForm.phone.trim() || null,
        accepted_rules: checkoutForm.accepted_rules,
        accepted_privacy: checkoutForm.accepted_privacy,
        source: "ticket-section",
      };

      const response = await axios.post(`${API}/payments/checkout/session`, payload);
      window.location.href = response.data.url;
    } catch (error) {
      const apiMessage = error.response?.data?.detail || "Checkout non disponibile al momento. Riprova tra poco.";
      setCheckoutState({ status: "error", message: apiMessage });
      toast.error(apiMessage);
    }
  };

  return (
    <div className="app-shell page-noise">
      <header className="site-header">
        <div className="mx-auto flex w-full max-w-[72rem] flex-col gap-4 px-[var(--container-px)] py-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center justify-between gap-4">
            <a href="#home" className="brand-lockup" data-testid="brand-home-link">
              <span className="brand-mark" aria-hidden="true">
                <Crown className="h-4 w-4" />
              </span>
              <div>
                <span className="wordmark">Fanta Royal</span>
                <p className="brand-tagline">Conquista la corona</p>
              </div>
            </a>

            <Button asChild={REGISTRATIONS_OPEN} className="header-cta md:hidden" disabled={!REGISTRATIONS_OPEN}>
              {REGISTRATIONS_OPEN ? (
                <a href="#ticket" data-testid="header-mobile-buy-ticket-button">Acquista ticket</a>
              ) : (
                <span data-testid="header-mobile-buy-ticket-button">Iscrizioni chiuse</span>
              )}
            </Button>
          </div>

          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-5">
            <nav className="no-scrollbar flex items-center gap-2 overflow-x-auto" aria-label="Navigazione principale">
              {NAV_ITEMS.map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  className={`nav-link ${activeSection === item.id ? "is-active" : ""}`}
                  aria-current={activeSection === item.id ? "page" : undefined}
                  data-testid={`nav-link-${item.id}`}
                >
                  {item.label}
                </a>
              ))}
            </nav>

            <Button asChild={REGISTRATIONS_OPEN} className="header-cta hidden md:inline-flex" disabled={!REGISTRATIONS_OPEN}>
              {REGISTRATIONS_OPEN ? (
                <a href="#ticket" data-testid="header-desktop-buy-ticket-button">Acquista ticket</a>
              ) : (
                <span data-testid="header-desktop-buy-ticket-button">Iscrizioni chiuse</span>
              )}
            </Button>
          </div>
        </div>
      </header>

      <main>
        <section id="home" className="section-shell hero-section">
          <div className="hero-backdrop" style={{ backgroundImage: `url(${HERO_BACKGROUND})` }} />
          <CrownMotif className="hero-crown" />

          <div className="mx-auto grid min-h-[calc(100vh-7rem)] w-full max-w-[72rem] gap-10 px-[var(--container-px)] py-14 sm:py-16 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:gap-12 lg:py-20">
            <motion.div {...heroAnimation} className="relative z-10 flex flex-col gap-7">
              <span className="hero-chip">Fantacalcio premium • ilfantaroyal.it</span>
              <div className="space-y-4">
                <p className="hero-subtitle">Conquista la corona</p>
                <h1 className="hero-title">Fanta Royal</h1>
                <p className="hero-copy">
                  Il fantacalcio premium dove ogni scelta può portarti sul trono.
                </p>
                <p className="hero-copy hero-copy--secondary">
                  Acquista il ticket ufficiale, consulta le pagine legali dedicate e preparati a una competizione con premi, struttura e tono da brand autorevole.
                </p>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <Button
                  className="primary-cta primary-cta--hero"
                  data-testid="hero-buy-ticket-button"
                  onClick={() => REGISTRATIONS_OPEN && scrollToSection("ticket")}
                  type="button"
                  disabled={!REGISTRATIONS_OPEN}
                >
                  {REGISTRATIONS_OPEN ? "Acquista ticket" : "Iscrizioni chiuse"}
                  <ShoppingBag className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  className="secondary-cta secondary-cta--hero"
                  data-testid="hero-secondary-cta-button"
                  onClick={() => scrollToSection("premi")}
                  type="button"
                >
                  Scopri i premi
                </Button>
              </div>

              <div className="hero-cta-meta">
                <p className="hero-cta-microcopy" data-testid="hero-cta-microcopy">
                  Pagamento sicuro con Stripe • {ticketConfig.display_price} • Carta, Apple Pay, Google Pay se disponibili • Dominio ufficiale {SITE_DOMAIN}.
                </p>
                <div className="hero-link-row">
                  <Link to={REGULATION_PATH} className="hero-regulation-link" data-testid="hero-regulation-link">
                    Leggi il regolamento
                  </Link>
                  <Link to={PRIVACY_PATH} className="hero-regulation-link" data-testid="hero-privacy-link">
                    Informativa privacy
                  </Link>
                </div>
              </div>

              <div className="hero-trust-grid">
                {HERO_TRUST_CHIPS.map((chip, index) => (
                  <div key={chip} className="hero-trust-chip" data-testid={`hero-trust-chip-${index + 1}`}>
                    <span className="hero-trust-dot" />
                    <span>{chip}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <Reveal className="relative z-10" delay={0.14}>
              <Card className="luxury-card hero-showcase">
                <CardContent className="p-6 sm:p-8">
                  <div className="showcase-topline">
                    <span className="section-kicker">Ticket ufficiale</span>
                    <span className="emerald-tag">Pagamento una tantum</span>
                  </div>

                  <div className="showcase-amount-block">
                    <p className="showcase-label">{ticketConfig.product_name}</p>
                    <p className="showcase-amount">{ticketConfig.display_price}</p>
                    <p className="showcase-copy">
                      Un ingresso premium in EUR, con trust legale visibile, prezzo controllato lato backend e conferma ordine solo dopo verifica Stripe via webhook.
                    </p>
                  </div>

                  <div className="showcase-mini-grid">
                    <div className="showcase-mini-card">
                      <p className="showcase-mini-kicker">Pagamenti accettati</p>
                      <h3 className="showcase-mini-title">Carta + wallet</h3>
                      <p className="showcase-mini-copy">Apple Pay e Google Pay compaiono automaticamente quando il dispositivo li supporta.</p>
                    </div>
                    <div className="showcase-mini-card">
                      <p className="showcase-mini-kicker">Compliance</p>
                      <h3 className="showcase-mini-title">Regolamento + Privacy</h3>
                      <p className="showcase-mini-copy">Le accettazioni richieste vengono richieste prima del checkout e archiviate nel flusso d’ordine.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Reveal>
          </div>
        </section>

        <section id="perche" className="section-shell content-section">
          <div className="mx-auto max-w-[72rem] px-[var(--container-px)]">
            <Reveal>
              <SectionHeading
                eyebrow="Perché Fanta Royal"
                title="Non un passatempo qualsiasi: una competizione pensata per chi vuole misurarsi sul serio."
                description="Fanta Royal unisce fantacalcio a premi, tensione sportiva, ticketing ordinato, pagine legali dedicate e posizionamento d’élite in un’esperienza costruita per risultare memorabile fin dal primo contatto."
              />
            </Reveal>

            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6" data-testid="value-proposition-section">
              {VALUE_CARDS.map((card, index) => {
                const Icon = card.icon;
                return (
                  <Reveal key={card.title} delay={index * 0.08}>
                    <Card className="luxury-card h-full" data-testid={`value-proposition-card-${index + 1}`}>
                      <CardContent className="p-6">
                        <div className="value-card-icon">
                          <Icon className="h-5 w-5" />
                        </div>
                        <h3 className="value-card-title">{card.title}</h3>
                        <p className="value-card-copy">{card.description}</p>
                      </CardContent>
                    </Card>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        <section id="premi" className="section-shell content-section prizes-section">
          <div className="mx-auto max-w-[72rem] px-[var(--container-px)]">
            <Reveal>
              <div className="prizes-shell">
                <CrownMotif className="prizes-crown" />
                <SectionHeading
                  eyebrow="I premi del trono"
                  title="Solo i migliori conquistano la gloria."
                  description="Il podio di Fanta Royal comunica gerarchia, prestigio e ricompensa reale. Il primo posto domina, gli altri inseguono."
                />

                <div className="mt-10 grid gap-5 lg:grid-cols-[0.88fr_1.24fr_0.88fr]" data-testid="prizes-podium">
                  {PRIZES.map((prize, index) => (
                    <Reveal key={prize.key} delay={index * 0.08} className={prize.orderClass}>
                      <article className={prize.className} data-testid={prize.testId}>
                        <div className="podium-topline">
                          <span className="podium-rank">{prize.rank}</span>
                          <Trophy className="h-5 w-5 text-[var(--fr-gold)]" />
                        </div>
                        <div className="podium-body">
                          <p className={`podium-value ${prize.key === "first" ? "podium-value--hero" : ""}`}>{prize.title}</p>
                          <h3 className="podium-title">{prize.label}</h3>
                          <p className="podium-copy">{prize.description}</p>
                        </div>
                      </article>
                    </Reveal>
                  ))}
                </div>

                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <Button asChild variant="outline" className="secondary-cta">
                    <Link to={REGULATION_PATH} data-testid="prizes-see-all-button">
                      Vedi tutti i premi
                    </Link>
                  </Button>
                  <p className="prizes-footnote">Nel regolamento web trovi anche premi dal 4° al 10° posto e tutte le competizioni collaterali aggiornate.</p>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section id="ticket" className="section-shell content-section ticket-section">
          <div className="mx-auto max-w-[72rem] px-[var(--container-px)]">
            <Reveal>
              <SectionHeading
                eyebrow="Ticket / Quota"
                title="Il ticket d’ingresso al trono"
                description="Un pagamento una tantum, elegante nel flusso e rigoroso nella conferma: il prezzo è definito lato backend, la parte legale è sempre visibile e l’ordine viene marcato come pagato solo dopo webhook Stripe."
              />
            </Reveal>

            <div className="ticket-grid mt-10">
              <Reveal>
                <Card className="luxury-card ticket-price-card">
                  <CardContent className="p-6 sm:p-8">
                    <div className="ticket-price-topline">
                      <span className="section-kicker">Ticket Fanta Royal</span>
                      <span className="emerald-tag">One-time payment</span>
                    </div>
                    <p className="ticket-price" data-testid="ticket-price-display">{ticketConfig.display_price}</p>
                    <p className="ticket-copy">Accesso a una competizione premium con checkout Stripe, UX mobile-first, pagine legali dedicate e struttura pronta per la pubblicazione su {SITE_DOMAIN}.</p>

                    <div className="ticket-benefits">
                      {ticketConfig.trust_points.map((point, index) => (
                        <div key={point} className="ticket-benefit" data-testid={`ticket-trust-point-${index + 1}`}>
                          <span className="hero-trust-dot" />
                          <span>{point}</span>
                        </div>
                      ))}
                    </div>

                    <div className="ticket-payment-methods">
                      <span className="payment-method-badge">Carta</span>
                      <span className="payment-method-badge">Apple Pay</span>
                      <span className="payment-method-badge">Google Pay</span>
                    </div>

                    <p className="checkout-trust-copy" data-testid="ticket-payment-trust-copy">
                      {ticketConfig.payment_methods_hint}
                    </p>
                  </CardContent>
                </Card>
              </Reveal>

              <Reveal delay={0.08}>
                <Card className="luxury-card ticket-checkout-card">
                  <CardContent className="p-6 sm:p-8">
                    {!REGISTRATIONS_OPEN ? (
                      <div className="registrations-closed-panel" data-testid="registrations-closed-message">
                        <div className="text-center space-y-4 py-8">
                          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[rgba(212,175,55,0.1)] mb-4">
                            <Shield className="w-8 h-8 text-[var(--fr-gold)]" />
                          </div>
                          <h3 className="text-2xl font-bold text-[var(--fr-ink)]">Iscrizioni chiuse</h3>
                          <p className="text-base text-[var(--fr-ink-muted)] max-w-md mx-auto">
                            {REGISTRATIONS_CLOSED_MESSAGE}
                          </p>
                          <div className="pt-4">
                            <Button asChild variant="outline" className="secondary-cta">
                              <Link to={REGULATION_PATH}>
                                Consulta il regolamento
                              </Link>
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                    <form className="space-y-5" onSubmit={handleCheckoutSubmit} data-testid="ticket-checkout-form">
                      <div className="legal-links-panel" data-testid="ticket-legal-links-panel">
                        <div>
                          <p className="checkout-note-title">Prima del checkout</p>
                          <p className="checkout-note-copy">Consulta le pagine legali ufficiali di Fanta Royal prima di continuare con il pagamento.</p>
                        </div>
                        <div className="legal-links-row">
                          <Link to={REGULATION_PATH} className="ticket-legal-link" data-testid="ticket-regulation-link">
                            Regolamento
                          </Link>
                          <Link to={PRIVACY_PATH} className="ticket-legal-link" data-testid="ticket-privacy-link">
                            Privacy Policy
                          </Link>
                        </div>
                      </div>

                      <div className="grid gap-5 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="ticket_first_name">Nome</Label>
                          <Input
                            id="ticket_first_name"
                            name="first_name"
                            value={checkoutForm.first_name}
                            onChange={handleCheckoutFieldChange}
                            placeholder="Il tuo nome"
                            className="form-input"
                            data-testid="ticket-form-first-name-input"
                            aria-invalid={Boolean(checkoutErrors.first_name)}
                          />
                          {checkoutErrors.first_name ? <p className="form-error">{checkoutErrors.first_name}</p> : null}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="ticket_last_name">Cognome</Label>
                          <Input
                            id="ticket_last_name"
                            name="last_name"
                            value={checkoutForm.last_name}
                            onChange={handleCheckoutFieldChange}
                            placeholder="Il tuo cognome"
                            className="form-input"
                            data-testid="ticket-form-last-name-input"
                            aria-invalid={Boolean(checkoutErrors.last_name)}
                          />
                          {checkoutErrors.last_name ? <p className="form-error">{checkoutErrors.last_name}</p> : null}
                        </div>
                      </div>

                      <div className="grid gap-5 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="ticket_email">Email</Label>
                          <Input
                            id="ticket_email"
                            name="email"
                            type="email"
                            value={checkoutForm.email}
                            onChange={handleCheckoutFieldChange}
                            placeholder="nome@email.it"
                            className="form-input"
                            data-testid="ticket-form-email-input"
                            aria-invalid={Boolean(checkoutErrors.email)}
                          />
                          {checkoutErrors.email ? <p className="form-error">{checkoutErrors.email}</p> : null}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="ticket_phone">Telefono facoltativo</Label>
                          <Input
                            id="ticket_phone"
                            name="phone"
                            value={checkoutForm.phone}
                            onChange={handleCheckoutFieldChange}
                            placeholder="+39 000 000 0000"
                            className="form-input"
                            data-testid="ticket-form-phone-input"
                            aria-invalid={Boolean(checkoutErrors.phone)}
                          />
                          {checkoutErrors.phone ? <p className="form-error">{checkoutErrors.phone}</p> : null}
                        </div>
                      </div>

                      <div className="checkout-note-block">
                        <p className="checkout-note-title">Messaggio di fiducia</p>
                        <p className="checkout-note-copy">
                          Il prezzo non viene mai deciso dal frontend. Stripe Checkout genera l’ordine e il database lo conferma solo dopo webhook verificato.
                        </p>
                      </div>

                      <div className="checkout-legal-panel">
                        <div className="checkout-legal-item">
                          <Checkbox
                            id="accepted_rules"
                            checked={checkoutForm.accepted_rules}
                            onCheckedChange={(checked) => handleCheckoutCheckboxChange("accepted_rules", checked)}
                            data-testid="ticket-form-rules-checkbox"
                            className="mt-1 h-5 w-5 rounded-md border-[rgba(212,175,55,0.35)] data-[state=checked]:bg-[var(--fr-gold)] data-[state=checked]:text-[#0b0d10]"
                          />
                          <div>
                            <p className="checkout-legal-label">
                              Dichiaro di aver letto e accettato il{" "}
                              <Link to={REGULATION_PATH} className="inline-link" data-testid="ticket-form-rules-link">
                                Regolamento di Fanta Royal
                              </Link>
                              .
                            </p>
                            {checkoutErrors.accepted_rules ? <p className="form-error">{checkoutErrors.accepted_rules}</p> : null}
                          </div>
                        </div>
                        <div className="checkout-legal-item">
                          <Checkbox
                            id="accepted_privacy"
                            checked={checkoutForm.accepted_privacy}
                            onCheckedChange={(checked) => handleCheckoutCheckboxChange("accepted_privacy", checked)}
                            data-testid="ticket-form-privacy-checkbox"
                            className="mt-1 h-5 w-5 rounded-md border-[rgba(212,175,55,0.35)] data-[state=checked]:bg-[var(--fr-gold)] data-[state=checked]:text-[#0b0d10]"
                          />
                          <div>
                            <p className="checkout-legal-label">
                              Dichiaro di aver letto l’{" "}
                              <Link to={PRIVACY_PATH} className="inline-link" data-testid="ticket-form-privacy-link-inline">
                                Informativa Privacy
                              </Link>
                              .
                            </p>
                            {checkoutErrors.accepted_privacy ? <p className="form-error">{checkoutErrors.accepted_privacy}</p> : null}
                          </div>
                        </div>
                      </div>

                      <Button
                        className="primary-cta ticket-submit-button"
                        type="submit"
                        disabled={checkoutState.status === "loading" || !ticketConfigLoaded || !isCheckoutLegallyReady}
                        data-testid="ticket-form-submit-button"
                      >
                        {checkoutState.status === "loading" ? "Preparazione checkout…" : "Acquista ticket"}
                      </Button>

                      {!isCheckoutLegallyReady ? (
                        <p className="checkout-lock-note" data-testid="ticket-legal-lock-note">
                          Per continuare devi aprire e accettare Regolamento e Privacy.
                        </p>
                      ) : null}

                      {checkoutState.status === "error" ? (
                        <p className="form-error-block" data-testid="ticket-form-error-message" aria-live="polite">
                          {checkoutState.message}
                        </p>
                      ) : null}
                    </form>
                    )}
                  </CardContent>
                </Card>
              </Reveal>
            </div>
          </div>
        </section>

        <section id="come-funziona" className="section-shell content-section">
          <div className="mx-auto max-w-[72rem] px-[var(--container-px)]">
            <Reveal>
              <SectionHeading
                eyebrow="Come funziona"
                title="Un percorso chiaro, premium e orientato alla conversione."
                description="L’acquisto del ticket, la lettura delle informazioni legali e l’accesso alla competizione sono pensati per risultare leggibili al primo sguardo e fluidi da mobile."
              />
            </Reveal>

            <div className="mt-10 grid gap-4 lg:grid-cols-4 lg:gap-6">
              {HOW_IT_WORKS.map((item, index) => {
                const Icon = item.icon;
                return (
                  <Reveal key={item.title} delay={index * 0.08}>
                    <Card className="luxury-card h-full" data-testid={`how-it-works-step-${index + 1}`}>
                      <CardContent className="p-6">
                        <div className="step-topline">
                          <span className="step-badge">{item.step}</span>
                          <Icon className="h-5 w-5 text-[var(--fr-gold)]" />
                        </div>
                        <h3 className="value-card-title">{item.title}</h3>
                        <p className="value-card-copy">{item.description}</p>
                      </CardContent>
                    </Card>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        <section id="per-chi" className="section-shell content-section audience-section">
          <div className="mx-auto max-w-[72rem] px-[var(--container-px)]">
            <Reveal>
              <SectionHeading
                eyebrow="Per chi è Fanta Royal"
                title="Per chi vuole strategia, prestigio e la tensione vera della competizione."
                description="Qui entrano utenti che vogliono più di un gioco: cercano ritmo, status, riconoscimento e la sensazione concreta di puntare a qualcosa di grande."
              />
            </Reveal>

            <div className="mt-10 grid gap-4 lg:grid-cols-3 lg:gap-6">
              {AUDIENCE_CARDS.map((card, index) => (
                <Reveal key={card.title} delay={index * 0.08}>
                  <Card className="luxury-card h-full" data-testid={`audience-profile-card-${index + 1}`}>
                    <CardContent className="p-6">
                      <h3 className="value-card-title">{card.title}</h3>
                      <p className="value-card-copy">{card.description}</p>
                    </CardContent>
                  </Card>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section id="lancio" className="section-shell content-section">
          <div className="mx-auto max-w-[72rem] px-[var(--container-px)]">
            <Reveal>
              <div className="launch-panel">
                <div className="space-y-4">
                  <span className="section-kicker">Il trono sta per aprirsi</span>
                  <h2 className="section-title max-w-3xl">Il prossimo assalto al trono</h2>
                  <p className="section-description max-w-2xl">
                    Il prossimo assalto al trono partirà dalla prima giornata di Serie A successiva alla chiusura del mercato reale estivo, con countdown fissato al <strong>4 settembre 2026</strong>. Chi vuole arrivare preparato può acquistare il ticket, consultare le pagine legali e posizionarsi prima dell’apertura ufficiale.
                  </p>
                </div>

                <CountdownBlock />

                <div className="launch-actions">
                  <Button
                    className="primary-cta"
                    data-testid="countdown-cta-button"
                    onClick={() => scrollToSection("ticket")}
                    type="button"
                  >
                    Acquista ticket
                  </Button>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="section-shell content-section trust-section">
          <div className="mx-auto max-w-[72rem] px-[var(--container-px)]">
            <Reveal>
              <div className="regulation-strip" data-testid="regulation-trust-strip">
                <div className="regulation-strip__intro">
                  <div className="regulation-strip__icon">
                    <FileText className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="regulation-strip__title">Trasparenza checkout, dominio ufficiale e pagine legali dedicate</p>
                    <p className="regulation-strip__copy">
                      Prezzo controllato lato backend, canonical ufficiale {SITE_DOMAIN}, ordine marcato come paid solo dopo conferma Stripe e pagine legali sempre accessibili durante il percorso di acquisto.
                    </p>
                  </div>
                </div>

                <div className="regulation-strip__points">
                  {REGULATION_POINTS.map((point, index) => (
                    <div key={point} className="regulation-point" data-testid={`regulation-point-${index + 1}`}>
                      <span className="hero-trust-dot" />
                      <span>{point}</span>
                    </div>
                  ))}
                </div>

                <div className="legal-links-row regulation-links-row">
                  <Link to={REGULATION_PATH} className="ticket-legal-link" data-testid="regulation-strip-link">
                    Consulta il regolamento
                  </Link>
                  <Link to={PRIVACY_PATH} className="ticket-legal-link" data-testid="regulation-strip-privacy-link">
                    Consulta la privacy policy
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section id="faq" className="section-shell content-section faq-section">
          <div className="mx-auto max-w-[72rem] px-[var(--container-px)]">
            <Reveal>
              <SectionHeading
                eyebrow="Domande frequenti"
                title="Chiarezza, prima ancora del fischio d’inizio."
                description="Ogni dettaglio importante è pensato per rassicurare, orientare e semplificare il percorso anche da smartphone."
              />
            </Reveal>

            <Reveal delay={0.08}>
              <Card className="luxury-card mt-10">
                <CardContent className="p-0 sm:p-2">
                  <Accordion type="single" collapsible className="px-4 sm:px-6" data-testid="faq-accordion">
                    {FAQ_ITEMS.map((item, index) => (
                      <AccordionItem key={item.question} value={`item-${index + 1}`} className="border-[rgba(255,255,255,0.08)]">
                        <AccordionTrigger
                          className="py-5 text-base font-semibold text-[var(--fr-ink)] hover:no-underline"
                          data-testid={`faq-trigger-${index + 1}`}
                        >
                          {item.question}
                        </AccordionTrigger>
                        <AccordionContent className="pb-5 text-sm leading-6 text-[var(--fr-ink-muted)]">
                          {item.answer}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
            </Reveal>
          </div>
        </section>

        <section id="contatti" className="section-shell content-section contact-section">
          <div className="mx-auto max-w-[72rem] px-[var(--container-px)]">
            <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr] lg:gap-8">
              <Reveal>
                <Card className="luxury-card h-full">
                  <CardContent className="p-6 sm:p-8">
                    <span className="section-kicker">Entra in Fanta Royal</span>
                    <h2 className="section-title">Richiedi informazioni o lascia i tuoi dati per ricevere aggiornamenti.</h2>
                    <p className="section-description mt-4">
                      Se vuoi entrare nella competizione ma preferisci prima un contatto diretto, qui trovi il canale ufficiale per informazioni e aggiornamenti.
                    </p>

                    <Separator className="my-6 bg-[rgba(212,175,55,0.16)]" />

                    <div className="space-y-4 text-sm text-[var(--fr-ink-muted)]">
                      <div className="contact-info-row">
                        <Mail className="h-4 w-4 text-[var(--fr-gold)]" />
                        <a href={`mailto:${CONTACT_EMAIL}`} className="transition-colors hover:text-[var(--fr-gold)]" data-testid="contact-section-email-link">
                          {CONTACT_EMAIL}
                        </a>
                      </div>
                      <div className="contact-info-row">
                        <Shield className="h-4 w-4 text-[var(--fr-gold)]" />
                        <span>Dominio ufficiale {SITE_DOMAIN} • flusso ticket con accettazioni legali e pagamento verificato.</span>
                      </div>
                      <div className="contact-info-row">
                        <Smartphone className="h-4 w-4 text-[var(--fr-gold)]" />
                        <span>Esperienza ottimizzata in mobile-first per restare leggibile, rapida e pulita.</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Reveal>

              <Reveal delay={0.08}>
                <Card className="luxury-card h-full">
                  <CardContent className="p-6 sm:p-8">
                    <form className="space-y-5" onSubmit={handleLeadSubmit} data-testid="lead-contact-form">
                      <div className="grid gap-5 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="lead_first_name">Nome</Label>
                          <Input
                            id="lead_first_name"
                            name="first_name"
                            value={leadFormState.first_name}
                            onChange={handleLeadFieldChange}
                            placeholder="Il tuo nome"
                            className="form-input"
                            data-testid="lead-form-first-name-input"
                            aria-invalid={Boolean(leadErrors.first_name)}
                          />
                          {leadErrors.first_name ? <p className="form-error">{leadErrors.first_name}</p> : null}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="lead_last_name">Cognome</Label>
                          <Input
                            id="lead_last_name"
                            name="last_name"
                            value={leadFormState.last_name}
                            onChange={handleLeadFieldChange}
                            placeholder="Il tuo cognome"
                            className="form-input"
                            data-testid="lead-form-last-name-input"
                            aria-invalid={Boolean(leadErrors.last_name)}
                          />
                          {leadErrors.last_name ? <p className="form-error">{leadErrors.last_name}</p> : null}
                        </div>
                      </div>

                      <div className="grid gap-5 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="lead_email">Email</Label>
                          <Input
                            id="lead_email"
                            name="email"
                            type="email"
                            value={leadFormState.email}
                            onChange={handleLeadFieldChange}
                            placeholder="nome@email.it"
                            className="form-input"
                            data-testid="lead-form-email-input"
                            aria-invalid={Boolean(leadErrors.email)}
                          />
                          {leadErrors.email ? <p className="form-error">{leadErrors.email}</p> : null}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="lead_phone">Telefono</Label>
                          <Input
                            id="lead_phone"
                            name="phone"
                            value={leadFormState.phone}
                            onChange={handleLeadFieldChange}
                            placeholder="+39 000 000 0000"
                            className="form-input"
                            data-testid="lead-form-phone-input"
                            aria-invalid={Boolean(leadErrors.phone)}
                          />
                          {leadErrors.phone ? <p className="form-error">{leadErrors.phone}</p> : null}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="lead_message">Messaggio facoltativo</Label>
                        <Textarea
                          id="lead_message"
                          name="message"
                          value={leadFormState.message}
                          onChange={handleLeadFieldChange}
                          placeholder="Raccontaci cosa vuoi sapere su Fanta Royal"
                          className="form-textarea"
                          data-testid="lead-form-message-textarea"
                        />
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-start gap-3 rounded-[var(--fr-radius-md)] border border-[rgba(212,175,55,0.14)] bg-[var(--fr-surface-2)] px-4 py-4">
                          <Checkbox
                            id="privacy_accepted"
                            checked={leadFormState.privacy_accepted}
                            onCheckedChange={handleLeadPrivacyChange}
                            className="mt-1 h-5 w-5 rounded-md border-[rgba(212,175,55,0.35)] data-[state=checked]:bg-[var(--fr-gold)] data-[state=checked]:text-[#0b0d10]"
                            data-testid="lead-form-privacy-checkbox"
                          />
                          <div>
                            <Label htmlFor="privacy_accepted" className="text-sm leading-6 text-[var(--fr-ink)]">
                              Accetto l’informativa privacy e autorizzo Fanta Royal a contattarmi per aggiornamenti e informazioni sulla competizione.
                            </Label>
                            {leadErrors.privacy_accepted ? <p className="form-error">{leadErrors.privacy_accepted}</p> : null}
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <Button
                          className="primary-cta min-w-[220px]"
                          type="submit"
                          disabled={leadSubmitState.status === "loading"}
                          data-testid="lead-form-submit-button"
                        >
                          {leadSubmitState.status === "loading" ? "Invio in corso…" : "Richiedi informazioni"}
                        </Button>
                        <p className="text-xs leading-5 text-[var(--fr-ink-muted)]">
                          Ti risponderemo con i prossimi dettagli utili e il posizionamento del lancio.
                        </p>
                      </div>

                      {leadSubmitState.status === "success" ? (
                        <p className="form-success" data-testid="lead-form-success-message" aria-live="polite">
                          {leadSubmitState.message}
                        </p>
                      ) : null}

                      {leadSubmitState.status === "error" ? (
                        <p className="form-error-block" data-testid="lead-form-error-message" aria-live="polite">
                          {leadSubmitState.message}
                        </p>
                      ) : null}
                    </form>
                  </CardContent>
                </Card>
              </Reveal>
            </div>
          </div>
        </section>
      </main>

      <footer className="footer-shell" data-testid="site-footer">
        <div className="footer-spotlight" />
        <div className="mx-auto max-w-[72rem] px-[var(--container-px)] py-10 sm:py-12">
          <div className="footer-feature-panel">
            <div>
              <span className="section-kicker">Fanta Royal</span>
              <h2 className="footer-display">Una competizione costruita per chi vuole entrare nel calcio fantasy con ambizione, fiducia e struttura pronta per il dominio ufficiale.</h2>
            </div>
            <Button
              className="primary-cta footer-cta"
              onClick={() => REGISTRATIONS_OPEN && scrollToSection("ticket")}
              type="button"
              data-testid="footer-primary-cta-button"
              disabled={!REGISTRATIONS_OPEN}
            >
              {REGISTRATIONS_OPEN ? "Acquista ticket" : "Iscrizioni chiuse"}
            </Button>
          </div>

          <div className="footer-grid">
            <div>
              <a href="#home" className="brand-lockup inline-flex" data-testid="footer-brand-home-link">
                <span className="brand-mark" aria-hidden="true">
                  <Crown className="h-4 w-4" />
                </span>
                <div>
                  <span className="wordmark">Fanta Royal</span>
                  <p className="brand-tagline">Conquista la corona</p>
                </div>
              </a>
              <p className="footer-copy">
                Un brand di fantacalcio premium pensato per chi cerca strategia, status, premi ufficiali e un percorso d’acquisto chiaro, legale e ordinato.
              </p>
            </div>

            <div>
              <p className="footer-title">Navigazione</p>
              <div className="footer-links">
                <a href="#home" className="footer-link" data-testid="footer-link-home">Home</a>
                <a href="#premi" className="footer-link" data-testid="footer-link-premi">Premi</a>
                <a href="#come-funziona" className="footer-link" data-testid="footer-link-come-funziona">Come funziona</a>
                <Link to={REGULATION_PATH} className="footer-link" data-testid="footer-link-regolamento">Regolamento</Link>
                <Link to={PRIVACY_PATH} className="footer-link" data-testid="footer-link-privacy">Privacy Policy</Link>
                <a href="#contatti" className="footer-link" data-testid="footer-link-contatti">Contatti</a>
              </div>
            </div>

            <div>
              <p className="footer-title">Trust e contatti</p>
              <div className="footer-links">
                <a href={`mailto:${CONTACT_EMAIL}`} className="footer-link" data-testid="footer-contact-email">
                  {CONTACT_EMAIL}
                </a>
                <span className="footer-link footer-link--static" data-testid="footer-official-domain">{SITE_DOMAIN}</span>
                <span className="footer-link footer-link--static">Pagamento una tantum • EUR • Stripe Checkout</span>
                <span className="footer-link footer-link--static">Regolamento e Privacy disponibili prima del checkout</span>
              </div>
            </div>
          </div>
        </div>
        <div className="footer-bottomline">
          <div className="mx-auto flex max-w-[72rem] flex-col gap-3 px-[var(--container-px)] py-5 text-xs text-[var(--fr-ink-muted)] md:flex-row md:items-center md:justify-between">
            <p>© {new Date().getFullYear()} Fanta Royal. Tutti i diritti riservati.</p>
            <div className="flex items-center gap-4">
              <p>Official domain ready: {SITE_DOMAIN} • canonical root domain with www redirect support.</p>
              <Link to="/admin" className="footer-admin-link" data-testid="footer-admin-link">
                Admin
              </Link>
            </div>
          </div>
        </div>
      </footer>

      <div className="mobile-cta-bar md:hidden" data-testid="mobile-sticky-cta-bar">
        <div>
          <p className="mobile-cta-label">Ticket ufficiale</p>
          <p className="mobile-cta-copy">{REGISTRATIONS_OPEN ? `${ticketConfig.display_price} • Stripe Checkout` : "Iscrizioni chiuse"}</p>
        </div>
        <Button 
          className="primary-cta mobile-cta-button" 
          onClick={() => REGISTRATIONS_OPEN && scrollToSection("ticket")} 
          type="button" 
          data-testid="mobile-sticky-buy-ticket-button"
          disabled={!REGISTRATIONS_OPEN}
        >
          {REGISTRATIONS_OPEN ? "Acquista" : "Chiuso"}
        </Button>
      </div>
    </div>
  );
}
